import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

import {
  applyMinMaxAreaAmount,
  getTierRangeString,
} from '../../helpers/functions/tiers';
import {
  getPluralAreaUnit,
  getShortAreaUnit,
  getSingularAreaUnit,
} from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import { StripeVolumePricingConfig } from '../../../../../shared/graphql/graphql';
import {
  getCurrencyForAreaUnit,
  getCurrencySignForAreaUnit,
} from '../../../../../shared/helpers/functions/currency';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import {
  MAX_AREA_AMOUNTS,
  MIN_ANNUAL_AREA_AMOUNTS,
} from '../../../../../shared/helpers/constants/areaAmounts';
import rootStore from '../../../rootStore/RootStore';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';

const TiersTable = observer(() => {
  const { t } = useTranslation();
  const { config } = usePriceConfig(rootStore);
  const { areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const currencySign = getCurrencySignForAreaUnit(areaUnit);

  const volumePricing = config?.AnnualSubscription
    .FullAccessToEssentialFunctionalities.prices[currency]
    .volumePricing as StripeVolumePricingConfig[];
  const minAreaAmount = MIN_ANNUAL_AREA_AMOUNTS[areaUnit];
  const maxAreaAmount = MAX_AREA_AMOUNTS[areaUnit];
  const adjustedVolumePricing = applyMinMaxAreaAmount(
    volumePricing,
    minAreaAmount,
    maxAreaAmount,
  );
  const areaUnitCode = getShortAreaUnit(areaUnit);
  const headerColumns = [
    t('plan-select.price-volume-discounts.columns.volume', {
      areaUnit: areaUnitCode,
    }),
    t('plan-select.price-volume-discounts.columns.price-per-unit', {
      areaUnit: areaUnitCode,
    }),
  ];

  const getRowStyles = (theme: Theme) => ({
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    '&:last-of-type': { border: 0 },
  });

  const handleContactUsClick = () => {
    AmplitudeAnalytics.trackContactUs();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
        {t('plan-select.price-volume-discounts.title')}
      </Typography>
      <TableContainer
        sx={(theme) => ({
          border: `1px solid ${theme.palette.grey[100]}`,
          borderRadius: '8px',
        })}
      >
        <Table>
          <TableHead>
            <TableRow sx={getRowStyles}>
              {headerColumns.map((column) => (
                <TableCell
                  key={column}
                  sx={(theme) => ({
                    p: '8px 12px',
                    fontSize: '12px',
                    lineHeight: '14px',
                    color: theme.palette.secondary.dark,
                    borderRight: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-of-type': { borderRight: 0 },
                  })}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {adjustedVolumePricing.map(({ min, max, price }, index) => (
              <TableRow key={index} sx={getRowStyles}>
                <TableCell
                  sx={(theme) => ({
                    p: '8px 12px',
                    fontSize: '12px',
                    lineHeight: '14px',
                    color: theme.palette.secondary.main,
                    borderRight: `1px solid ${theme.palette.grey[100]}`,
                    borderBottom: 'none',
                  })}
                >
                  {getTierRangeString(min!, max)}
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    p: '8px 12px',
                    fontSize: '12px',
                    lineHeight: '14px',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    borderBottom: 'none',
                  })}
                >
                  {price ? (
                    `${currencySign}${price}`
                  ) : (
                    <Link
                      href={import.meta.env.VITE_DEMO_FORM_URL}
                      sx={(theme) => ({
                        color: theme.palette.secondary.main,
                        fontWeight: 400,
                      })}
                      onClick={handleContactUsClick}
                    >
                      {t('plan-select.price-volume-discounts.contact-us')}
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body1">
        <Trans
          i18nKey={'plan-select.price-volume-discounts.description'}
          values={{
            areaUnit: getSingularAreaUnit(areaUnit),
            areaUnits: getPluralAreaUnit(areaUnit),
            areaUnitCode,
            currency: currencySign,
            volume1: adjustedVolumePricing?.[0]?.max,
            price1: adjustedVolumePricing?.[0]?.price,
            volume2:
              (adjustedVolumePricing?.[1]?.max ?? 0) -
              (adjustedVolumePricing?.[0]?.max ?? 0),
            price2: adjustedVolumePricing?.[1]?.price,
          }}
        >
          Text
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              fontWeight: 500,
            })}
          >
            Text
          </Box>
          Text
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              fontWeight: 500,
            })}
          >
            Text
          </Box>
        </Trans>
      </Typography>
    </Box>
  );
});

export default TiersTable;
